import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { AdminTabs } from './VoiceAdmin';

type SequenceRow = {
  id: string;
  name: string;
  trigger: string;
  defaultSubject: string;
  templateDocId: string;
  functionPath: string;
  scheduleConfigDocId?: string;
  variables: string[];
};

type BannerMessage = {
  type: 'success' | 'error' | 'info';
  text: string;
};

type AdminViewTab = 'scoreboard' | 'sequences';
type ScoreboardRangePreset = 'last_7_days' | 'last_14_days' | 'last_30_days' | 'yesterday' | 'today' | 'custom';

type ScoreboardDateRange = {
  preset: ScoreboardRangePreset;
  start: string;
  end: string;
  label: string;
  daysBack: number;
};

type ScoreboardTier =
  | 'paid'
  | 'trial_started'
  | 'speech_momentum'
  | 'first_speech'
  | 'paywall_intent'
  | 'onboarding'
  | 'new_user';

type ScoreboardNextEmail = {
  sequenceId: string;
  label: string;
  reason: string;
};

type ScoreboardSignals = {
  createdAt: number | null;
  lastOpenedAt: number | null;
  onboardingCompletedAt: number | null;
  latestSpeechAt: number | null;
  totalSpeeches: number;
  currentStreak: number;
  averageScore: number | null;
  hasAppAccess: boolean;
  trialStartedAt: number | null;
  trialEndsAt: number | null;
  paidAt: number | null;
  paywallViewCount: number;
  checkoutStartedAt: number | null;
  offerClickedAt: number | null;
  offerConvertedAt: number | null;
  emailSentCount: number;
  emailOpenedCount: number;
  emailClickedCount: number;
  purchaseAttemptCount: number;
  purchaseSuccessCount: number;
  purchaseFailedCount: number;
  purchaseCanceledCount: number;
};

type ScoreboardUser = {
  id: string;
  email: string;
  displayName: string;
  tier: ScoreboardTier;
  tierLabel: string;
  priority: number;
  suggestedLane: string;
  nextEmail: ScoreboardNextEmail | null;
  signals: ScoreboardSignals;
};

type ScoreboardState = {
  loading: boolean;
  error: string;
  loadedAt: Date | null;
  users: ScoreboardUser[];
  appsFlyerSummary: Record<string, any> | null;
  emailLogCount: number;
  purchaseLogCount: number;
  userLimit: number;
};

type AppsFlyerAggregatePeriodDoc = Record<string, any> & {
  id: string;
  periodStart?: unknown;
  periodEnd?: unknown;
  summary?: Record<string, any>;
  excludedFromRangeRollups?: boolean;
};

const SCOREBOARD_USER_LIMIT = 300;
const SCOREBOARD_LOG_LIMIT = 500;
const SCOREBOARD_DEFAULT_RANGE_PRESET: ScoreboardRangePreset = 'last_7_days';
const SCOREBOARD_RANGE_OPTIONS: Array<{ value: ScoreboardRangePreset; label: string; daysBack: number }> = [
  { value: 'last_7_days', label: 'Last 7 days', daysBack: 7 },
  { value: 'last_14_days', label: 'Last 14 days', daysBack: 14 },
  { value: 'last_30_days', label: 'Last 30 days', daysBack: 30 },
  { value: 'yesterday', label: 'Yesterday', daysBack: 1 },
  { value: 'today', label: 'Today', daysBack: 1 },
  { value: 'custom', label: 'Custom', daysBack: 0 },
];

const PARROT_APPSFLYER_APP_OPEN_EVENTS = ['af_app_opened'];
const PARROT_APPSFLYER_REGISTRATION_EVENTS = ['af_complete_registration', 'parrot_signin_succeeded'];
const PARROT_APPSFLYER_ONBOARDING_EVENTS = ['parrot_onboarding_completed'];
const PARROT_APPSFLYER_RECORD_EVENTS = ['parrot_record_completed'];
const PARROT_APPSFLYER_SCORE_EVENTS = ['parrot_score_received'];
const PARROT_APPSFLYER_PAYWALL_EVENTS = ['parrot_paywall_viewed', 'af_content_view'];
const PARROT_APPSFLYER_CTA_EVENTS = ['parrot_subscription_cta_pressed'];
const PARROT_APPSFLYER_PLAN_SELECTED_EVENTS = ['parrot_subscription_plan_selected'];
const PARROT_APPSFLYER_CHECKOUT_EVENTS = ['af_initiated_checkout'];
const PARROT_APPSFLYER_TRIAL_EVENTS = ['af_start_trial'];
const PARROT_APPSFLYER_PURCHASE_EVENTS = ['af_purchase', 'af_subscribe'];
const PARROT_APPSFLYER_CANCEL_EVENTS = ['parrot_subscription_purchase_cancelled'];

const SEQUENCES: SequenceRow[] = [
  {
    id: 'parrot-welcome-v1',
    name: 'Welcome to Parrot',
    trigger: 'After account creation or first onboarding start.',
    defaultSubject: 'Welcome to Parrot - your speech gym is open',
    templateDocId: 'parrot-welcome-v1',
    functionPath: '/api/send-parrot-sequence-email',
    variables: ['firstName', 'practiceUrl'],
  },
  {
    id: 'parrot-first-speech-nudge-v1',
    name: 'First Speech Nudge',
    trigger: '24 hours after signup when no completed speech exists.',
    defaultSubject: 'Your first 60-second speech is waiting',
    templateDocId: 'parrot-first-speech-nudge-v1',
    functionPath: '/api/send-parrot-sequence-email',
    scheduleConfigDocId: 'parrot-first-speech-nudge-v1',
    variables: ['firstName', 'practiceUrl'],
  },
  {
    id: 'parrot-score-ready-v1',
    name: 'Score Ready',
    trigger: 'When a speech score finishes processing.',
    defaultSubject: 'Your Parrot score is ready',
    templateDocId: 'parrot-score-ready-v1',
    functionPath: '/api/send-parrot-sequence-email',
    variables: ['firstName', 'score', 'prompt', 'practiceUrl'],
  },
  {
    id: 'parrot-streak-save-v1',
    name: 'Streak Rescue',
    trigger: 'When a user has not practiced today and their streak is at risk.',
    defaultSubject: 'Keep your speaking streak alive',
    templateDocId: 'parrot-streak-save-v1',
    functionPath: '/api/send-parrot-sequence-email',
    scheduleConfigDocId: 'parrot-streak-save-v1',
    variables: ['firstName', 'currentStreak', 'practiceUrl'],
  },
  {
    id: 'parrot-level-up-v1',
    name: 'Level Up',
    trigger: 'After reaching the next parrot evolution level.',
    defaultSubject: 'You leveled up to {{levelName}}',
    templateDocId: 'parrot-level-up-v1',
    functionPath: '/api/send-parrot-sequence-email',
    variables: ['firstName', 'levelName', 'totalSpeeches', 'practiceUrl'],
  },
  {
    id: 'parrot-trial-ending-v1',
    name: 'Trial Ending',
    trigger: '24 hours before a trial expires.',
    defaultSubject: 'Your Parrot trial ends soon',
    templateDocId: 'parrot-trial-ending-v1',
    functionPath: '/api/send-parrot-sequence-email',
    scheduleConfigDocId: 'parrot-trial-ending-v1',
    variables: ['firstName', 'trialEndsAt', 'subscriptionUrl'],
  },
  {
    id: 'parrot-winback-v1',
    name: 'Inactivity Winback',
    trigger: '3, 7, and 14 days after the last completed speech.',
    defaultSubject: 'A fresh prompt is waiting in Parrot',
    templateDocId: 'parrot-winback-v1',
    functionPath: '/api/send-parrot-sequence-email',
    scheduleConfigDocId: 'parrot-winback-v1',
    variables: ['firstName', 'daysInactive', 'practiceUrl'],
  },
  {
    id: 'parrot-legacy-welcome-back-v1',
    name: 'Legacy Welcome Back',
    trigger: 'When a returning legacy Parrot user opens the rebuilt app.',
    defaultSubject: 'Parrot got rebuilt for you',
    templateDocId: 'parrot-legacy-welcome-back-v1',
    functionPath: '/api/send-parrot-sequence-email',
    variables: ['firstName', 'legacySpeechesLeft', 'practiceUrl'],
  },
  {
    id: 'parrot-legacy-winback-free-month-v1',
    name: 'Legacy Free Month Winback',
    trigger: 'Campaign email for legacy Parrot members without active access.',
    defaultSubject: 'Parrot got rebuilt. Your free month is waiting',
    templateDocId: 'parrot-legacy-winback-free-month-v1',
    functionPath: '/api/send-parrot-sequence-email',
    scheduleConfigDocId: 'parrot-legacy-winback-free-month-v1',
    variables: ['firstName', 'userId', 'plan', 'offerUrl', 'subscriptionUrl'],
  },
];

const DEFAULT_VARS = {
  firstName: 'Tremaine',
  first_name: 'Tremaine',
  practiceUrl: 'https://speakparrot.com',
  subscriptionUrl: 'https://speakparrot.com',
  offerUrl: 'https://speakparrot.com/offer',
  userId: 'replace-with-user-id',
  plan: 'monthly',
  score: '87',
  prompt: 'Tell us about a habit you broke. What replaced it?',
  currentStreak: '4',
  levelName: 'African Grey',
  totalSpeeches: '24',
  trialEndsAt: 'tomorrow',
  daysInactive: '7',
  legacySpeechesLeft: '12',
};

function starterHtml(seq: SequenceRow) {
  const year = new Date().getFullYear();
  const ctaHref = sequenceCtaHref(seq.id);
  const ctaLabel = sequenceCtaLabel(seq.id);
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(seq.defaultSubject)}</title>
  </head>
  <body style="margin:0;padding:0;background:#f5f0df;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;color:#111;">
    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background:#f5f0df;padding:28px 12px;">
      <tr>
        <td align="center">
          <table role="presentation" cellpadding="0" cellspacing="0" width="620" style="max-width:620px;width:100%;background:#fff;border:1px solid #e8dfc7;border-radius:18px;overflow:hidden;">
            <tr>
              <td style="padding:24px 24px 8px 24px;">
                <div style="font-size:13px;letter-spacing:0.16em;text-transform:uppercase;color:#777;font-weight:800;">Parrot</div>
                <h1 style="margin:12px 0 10px 0;font-size:30px;line-height:1.12;color:#111;font-weight:900;">Hi {{firstName}},</h1>
                <p style="margin:0 0 18px 0;font-size:16px;line-height:1.65;color:#333;">
                  ${escapeHtml(sequenceBodyCopy(seq.id))}
                </p>
                <a href="${ctaHref}" style="display:inline-block;background:#111;color:#fff;text-decoration:none;font-weight:800;font-size:15px;padding:13px 18px;border-radius:12px;">
                  ${ctaLabel}
                </a>
              </td>
            </tr>
            <tr>
              <td style="padding:18px 24px 24px 24px;">
                <p style="margin:0;font-size:12px;line-height:1.6;color:#777;">
                  You are receiving this because you created a Parrot account or use Parrot speech practice.
                  Reply to this email if you need help.
                </p>
              </td>
            </tr>
          </table>
          <div style="font-size:11px;color:#8b836f;margin-top:16px;">(c) ${year} Parrot</div>
        </td>
      </tr>
    </table>
  </body>
</html>`;
}

function sequenceCtaHref(sequenceId: string) {
  if (sequenceId === 'parrot-legacy-winback-free-month-v1') return '{{offerUrl}}';
  if (sequenceId === 'parrot-trial-ending-v1') return '{{subscriptionUrl}}';
  return '{{practiceUrl}}';
}

function sequenceCtaLabel(sequenceId: string) {
  if (sequenceId === 'parrot-legacy-winback-free-month-v1') return 'Claim your free month';
  if (sequenceId === 'parrot-trial-ending-v1') return 'Keep Parrot active';
  return 'Open Parrot';
}

function sequenceBodyCopy(sequenceId: string) {
  switch (sequenceId) {
    case 'parrot-first-speech-nudge-v1':
      return 'Your first 60-second speech is still the most important one. Open Parrot, grab a prompt, and speak for sixty seconds.';
    case 'parrot-score-ready-v1':
      return 'Your latest speech score is ready. Open Parrot to see the score recap, transcript highlights, and what to improve in your next speech.';
    case 'parrot-streak-save-v1':
      return 'Your streak is still alive today. One short speech keeps the chain moving.';
    case 'parrot-level-up-v1':
      return 'You just reached {{levelName}}. That progress came from showing up, speaking out loud, and completing each timed speech with intention.';
    case 'parrot-trial-ending-v1':
      return 'Your trial ends {{trialEndsAt}}. Keep your speech gym open so your practice speeches, scores, and streaks continue.';
    case 'parrot-winback-v1':
      return 'It has been {{daysInactive}} days since your last speech. A fresh prompt is waiting when you are ready.';
    case 'parrot-legacy-welcome-back-v1':
      return 'We rebuilt Parrot from the ground up with richer scoring and better progress loops. Your old progress still matters here.';
    case 'parrot-legacy-winback-free-month-v1':
      return 'Parrot has a new UI, clearer speech feedback, speaking challenges, and friend speak-offs. Your free month is waiting so you can come back and try the new experience.';
    default:
      return 'Your speech gym is ready. Practice for sixty seconds, get scored, and keep building confidence with one completed speech at a time.';
  }
}

function escapeHtml(input: string) {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function prettyDate(value: any) {
  if (!value) return '';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

function sequenceConfigDocId(seq: SequenceRow) {
  return seq.scheduleConfigDocId || seq.id;
}

function asRecord(value: unknown): Record<string, any> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, any>
    : null;
}

function scoreboardString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function scoreboardNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[$,%]/g, '').replace(/,/g, '').trim();
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function scoreboardBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['true', 'yes', '1', 'active', 'trialing', 'subscribed'].includes(normalized);
  }
  return false;
}

function scoreboardMillis(value: unknown): number | null {
  if (!value) return null;
  if (value instanceof Date) {
    const millis = value.getTime();
    return Number.isNaN(millis) ? null : millis;
  }
  if (typeof value === 'number') {
    const millis = value < 1_000_000_000_000 ? value * 1000 : value;
    return Number.isFinite(millis) ? millis : null;
  }
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && value.trim()) return scoreboardMillis(numeric);
    const millis = Date.parse(value);
    return Number.isNaN(millis) ? null : millis;
  }
  if (typeof value === 'object') {
    const maybeTimestamp = value as { toDate?: () => Date; seconds?: number };
    if (typeof maybeTimestamp.toDate === 'function') return scoreboardMillis(maybeTimestamp.toDate());
    if (typeof maybeTimestamp.seconds === 'number') return maybeTimestamp.seconds * 1000;
  }
  return null;
}

function latestMillis(...values: unknown[]): number | null {
  const candidates = values
    .map(scoreboardMillis)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  return candidates.length ? Math.max(...candidates) : null;
}

function getNestedValue(source: Record<string, any> | null | undefined, path: string): unknown {
  if (!source) return undefined;
  return path.split('.').reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') return undefined;
    return (current as Record<string, any>)[key];
  }, source);
}

function dateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function buildScoreboardDateRange(preset: ScoreboardRangePreset): ScoreboardDateRange {
  const today = new Date();
  const option = SCOREBOARD_RANGE_OPTIONS.find((row) => row.value === preset) || SCOREBOARD_RANGE_OPTIONS[0];
  if (preset === 'yesterday') {
    const yesterday = addDays(today, -1);
    return {
      preset,
      start: dateInputValue(yesterday),
      end: dateInputValue(yesterday),
      label: option.label,
      daysBack: 1,
    };
  }
  if (preset === 'today') {
    return {
      preset,
      start: dateInputValue(today),
      end: dateInputValue(today),
      label: option.label,
      daysBack: 1,
    };
  }
  const daysBack = Math.max(1, option.daysBack || 7);
  return {
    preset,
    start: dateInputValue(addDays(today, -(daysBack - 1))),
    end: dateInputValue(today),
    label: option.label,
    daysBack,
  };
}

function dateOnlyDaySpan(start: string, end: string): number {
  const startMillis = Date.parse(`${start}T00:00:00`);
  const endMillis = Date.parse(`${end}T00:00:00`);
  if (!Number.isFinite(startMillis) || !Number.isFinite(endMillis)) return 0;
  return Math.max(1, Math.round((endMillis - startMillis) / 86_400_000) + 1);
}

function millisInDateRange(value: unknown, range: ScoreboardDateRange): boolean {
  const millis = scoreboardMillis(value);
  if (!millis) return false;
  const startMillis = Date.parse(`${range.start}T00:00:00`);
  const endMillis = Date.parse(`${range.end}T23:59:59.999`);
  return millis >= startMillis && millis <= endMillis;
}

function formatDateOnlyLabel(value: string): string {
  const millis = Date.parse(`${value}T00:00:00`);
  if (!Number.isFinite(millis)) return value || 'N/A';
  return new Date(millis).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatRangeLabel(range: ScoreboardDateRange): string {
  if (range.start === range.end) return formatDateOnlyLabel(range.start);
  return `${formatDateOnlyLabel(range.start)} to ${formatDateOnlyLabel(range.end)}`;
}

function formatScoreboardDate(value: unknown): string {
  const millis = scoreboardMillis(value);
  if (!millis) return 'Not seen';
  return new Date(millis).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatScoreboardAgo(value: unknown): string {
  const millis = scoreboardMillis(value);
  if (!millis) return 'not seen';
  const diff = Math.max(0, Date.now() - millis);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatCompactNumber(value: number): string {
  return Math.round(value).toLocaleString();
}

function formatCurrency(value: number): string {
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value >= 100 ? 0 : 2,
  });
}

function formatPercent(numerator: number, denominator: number): string {
  if (!denominator) return '0%';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

function topEntriesFromMap(source: Record<string, number>, limitCount = 6): Array<[string, number]> {
  return Object.entries(source)
    .filter(([, value]) => Number(value) > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limitCount);
}

function titleizeToken(value: string): string {
  return value
    .replace(/^parrot_/, '')
    .replace(/^af_/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function emailLogMillis(log: Record<string, any>): number | null {
  return latestMillis(log.updatedAt, log.lastEventAt, log.sentAt, log.createdAt);
}

function purchaseLogMillis(log: Record<string, any>): number | null {
  return latestMillis(log.createdAtEpoch, log.createdAt, log.updatedAt);
}

function matchesUserOrEmail(log: Record<string, any>, userId: string, email: string): boolean {
  const logUserId = scoreboardString(log.userId || log.uid || getNestedValue(log, 'custom.userId'));
  const logEmail = scoreboardString(log.toEmail || log.email).toLowerCase();
  return (!!logUserId && logUserId === userId) || (!!email && logEmail === email.toLowerCase());
}

function isParrotEmailLog(log: Record<string, any>): boolean {
  const product = scoreboardString(log.product).toLowerCase();
  const sequenceId = scoreboardString(log.sequenceId || log.campaignId);
  return product === 'parrot' || sequenceId.startsWith('parrot-');
}

function purchaseStatus(log: Record<string, any>): 'attempted' | 'success' | 'failed' | 'canceled' | 'unknown' {
  const raw = scoreboardString(log.purchaseStatus || log.status).toLowerCase();
  if (raw === 'attempted' || raw === 'success' || raw === 'failed' || raw === 'canceled') return raw;
  if (raw.includes('cancel')) return 'canceled';
  if (raw.includes('fail')) return 'failed';
  if (raw.includes('success') || raw.includes('paid') || raw.includes('active')) return 'success';
  return 'unknown';
}

function userDisplayName(data: Record<string, any>, fallbackEmail: string, id: string): string {
  return scoreboardString(data.displayName)
    || scoreboardString(data.name)
    || scoreboardString(data.firstName)
    || (fallbackEmail ? fallbackEmail.split('@')[0] : '')
    || id.slice(0, 8);
}

function userEmail(data: Record<string, any>): string {
  return scoreboardString(data.email || data.authEmail || data.appleEmail || getNestedValue(data, 'profile.email')).toLowerCase();
}

function userTotalSpeeches(data: Record<string, any>): number {
  return Math.max(
    scoreboardNumber(data.totalSpeeches) || 0,
    scoreboardNumber(data.speechCount) || 0,
    scoreboardNumber(data.recordingCount) || 0,
    scoreboardNumber(getNestedValue(data, 'recordingStats.total')) || 0,
    scoreboardNumber(getNestedValue(data, 'recordingStats.totalSpeeches')) || 0
  );
}

function userAverageScore(data: Record<string, any>): number | null {
  return scoreboardNumber(data.averageScore)
    ?? scoreboardNumber(data.lastScore)
    ?? scoreboardNumber(getNestedValue(data, 'scoreStats.average'))
    ?? scoreboardNumber(getNestedValue(data, 'scores.average'));
}

function userHasAppAccess(data: Record<string, any>): boolean {
  const subscription = asRecord(data.subscription);
  return scoreboardBoolean(data.subscriptionIsActive)
    || scoreboardBoolean(data.isPro)
    || scoreboardBoolean(data.hasActiveAccess)
    || scoreboardBoolean(data.isLegacyUser)
    || scoreboardBoolean(subscription?.isActive)
    || scoreboardBoolean(subscription?.active)
    || ['active', 'trialing'].includes(scoreboardString(subscription?.status).toLowerCase());
}

function tierLabel(tier: ScoreboardTier): string {
  switch (tier) {
    case 'paid': return 'Paid / active';
    case 'trial_started': return 'Trial started';
    case 'speech_momentum': return 'Speech momentum';
    case 'first_speech': return 'First speech';
    case 'paywall_intent': return 'Paywall intent';
    case 'onboarding': return 'Onboarding complete';
    case 'new_user':
    default:
      return 'New user';
  }
}

function buildScoreboardUser(args: {
  id: string;
  data: Record<string, any>;
  emailLogs: Record<string, any>[];
  purchaseLogs: Record<string, any>[];
}): ScoreboardUser {
  const { id, data, emailLogs, purchaseLogs } = args;
  const email = userEmail(data);
  const sequenceState = asRecord(data.parrotEmailSequenceState) || {};
  const subscription = asRecord(data.subscription) || {};
  const successfulPurchases = purchaseLogs.filter((log) => purchaseStatus(log) === 'success');
  const failedPurchases = purchaseLogs.filter((log) => purchaseStatus(log) === 'failed');
  const canceledPurchases = purchaseLogs.filter((log) => purchaseStatus(log) === 'canceled');
  const sentLogs = emailLogs.filter((log) => ['sent', 'delivered', 'opened', 'clicked'].includes(scoreboardString(log.status || log.lastEvent).toLowerCase()));
  const openedLogs = emailLogs.filter((log) => scoreboardMillis(log.openedAt) || scoreboardNumber(log.openCount));
  const clickedLogs = emailLogs.filter((log) => scoreboardMillis(log.clickedAt) || scoreboardNumber(log.clickCount));

  const totalSpeeches = userTotalSpeeches(data);
  const latestSpeechAt = latestMillis(
    data.lastSpeechAt,
    data.lastRecordingAt,
    data.lastCompletedSpeechAt,
    getNestedValue(data, 'recordingStats.lastRecordedAt'),
    getNestedValue(data, 'recordingStats.latestRecordedAt')
  );
  const onboardingCompletedAt = latestMillis(
    data.onboardingCompletedAt,
    data.onboardedAt,
    data.completedOnboardingAt,
    data.updatedAt
  );
  const completedOnboarding = Boolean(data.onboarded || data.hasCompletedOnboarding || data.onboardingCompletedAt || data.onboardedAt);
  const paidAt = latestMillis(
    data.paidAt,
    data.subscriptionStartedAt,
    subscription.startedAt,
    subscription.updatedAt,
    ...successfulPurchases.map(purchaseLogMillis)
  );
  const trialStartedAt = latestMillis(data.trialStartedAt, subscription.trialStartedAt, subscription.createdAt);
  const hasAppAccess = userHasAppAccess(data) || Boolean(successfulPurchases.length);
  const paywallViewCount = Math.max(
    scoreboardNumber(data.paywallViewCount) || 0,
    scoreboardNumber(getNestedValue(data, 'paywall.viewCount')) || 0,
    scoreboardNumber(getNestedValue(data, 'parrotPaywall.viewCount')) || 0
  );
  const checkoutStartedAt = latestMillis(
    sequenceState.legacyWinbackCheckoutStartedAt,
    data.checkoutStartedAt,
    ...purchaseLogs.map(purchaseLogMillis)
  );
  const offerClickedAt = latestMillis(sequenceState.legacyWinbackClickedAt);
  const offerConvertedAt = latestMillis(sequenceState.legacyWinbackConvertedAt);
  const currentStreak = Math.max(
    scoreboardNumber(data.currentStreak) || 0,
    scoreboardNumber(getNestedValue(data, 'streak.current')) || 0
  );

  const signals: ScoreboardSignals = {
    createdAt: scoreboardMillis(data.createdAt),
    lastOpenedAt: latestMillis(data.lastOpened, data.lastOpenedAt, data.updatedAt),
    onboardingCompletedAt: completedOnboarding ? onboardingCompletedAt : null,
    latestSpeechAt,
    totalSpeeches,
    currentStreak,
    averageScore: userAverageScore(data),
    hasAppAccess,
    trialStartedAt,
    trialEndsAt: latestMillis(data.trialEndsAt, subscription.trialEndsAt),
    paidAt,
    paywallViewCount,
    checkoutStartedAt,
    offerClickedAt,
    offerConvertedAt,
    emailSentCount: sentLogs.length,
    emailOpenedCount: openedLogs.length,
    emailClickedCount: clickedLogs.length,
    purchaseAttemptCount: purchaseLogs.length,
    purchaseSuccessCount: successfulPurchases.length,
    purchaseFailedCount: failedPurchases.length,
    purchaseCanceledCount: canceledPurchases.length,
  };

  let tier: ScoreboardTier = 'new_user';
  if (signals.hasAppAccess || signals.paidAt) tier = 'paid';
  else if (signals.trialStartedAt || signals.trialEndsAt) tier = 'trial_started';
  else if (signals.totalSpeeches >= 3 || signals.currentStreak >= 2) tier = 'speech_momentum';
  else if (signals.totalSpeeches > 0) tier = 'first_speech';
  else if (signals.paywallViewCount > 0 || signals.checkoutStartedAt || signals.purchaseAttemptCount > 0) tier = 'paywall_intent';
  else if (signals.onboardingCompletedAt) tier = 'onboarding';

  const nextEmail = suggestedNextEmail(tier, signals);
  return {
    id,
    email,
    displayName: userDisplayName(data, email, id),
    tier,
    tierLabel: tierLabel(tier),
    priority: scoreboardPriority(tier, signals),
    suggestedLane: suggestedLane(tier),
    nextEmail,
    signals,
  };
}

function suggestedLane(tier: ScoreboardTier): string {
  switch (tier) {
    case 'paid': return 'Retention';
    case 'trial_started': return 'Trial activation';
    case 'speech_momentum': return 'Progress celebration';
    case 'first_speech': return 'Score follow-up';
    case 'paywall_intent': return 'Subscription recovery';
    case 'onboarding': return 'First speech nudge';
    case 'new_user':
    default:
      return 'Welcome flow';
  }
}

function suggestedNextEmail(tier: ScoreboardTier, signals: ScoreboardSignals): ScoreboardNextEmail | null {
  if (tier === 'paid') return null;
  if (tier === 'trial_started') {
    return {
      sequenceId: 'parrot-trial-ending-v1',
      label: 'Trial Ending',
      reason: signals.trialEndsAt ? 'Trial end date is present.' : 'Trial state is active.',
    };
  }
  if (tier === 'speech_momentum') {
    return {
      sequenceId: 'parrot-streak-save-v1',
      label: 'Streak Rescue',
      reason: 'User has speech momentum and should stay active.',
    };
  }
  if (tier === 'first_speech') {
    return {
      sequenceId: 'parrot-score-ready-v1',
      label: 'Score Ready',
      reason: 'User has completed at least one timed speech.',
    };
  }
  if (tier === 'paywall_intent') {
    return {
      sequenceId: 'parrot-legacy-winback-free-month-v1',
      label: 'Legacy Free Month Winback',
      reason: 'User showed subscription intent without active access.',
    };
  }
  if (tier === 'onboarding') {
    return {
      sequenceId: 'parrot-first-speech-nudge-v1',
      label: 'First Speech Nudge',
      reason: 'Onboarding is complete, but no completed speech is visible.',
    };
  }
  return {
    sequenceId: 'parrot-welcome-v1',
    label: 'Welcome to Parrot',
    reason: 'User is early in the Parrot journey.',
  };
}

function scoreboardPriority(tier: ScoreboardTier, signals: ScoreboardSignals): number {
  const base = {
    paid: 20,
    trial_started: 90,
    speech_momentum: 75,
    first_speech: 70,
    paywall_intent: 85,
    onboarding: 60,
    new_user: 45,
  }[tier];
  return base
    + Math.min(10, signals.purchaseAttemptCount * 3)
    + Math.min(8, signals.emailClickedCount * 2)
    + Math.min(8, signals.paywallViewCount);
}

function userHasRangeActivity(user: ScoreboardUser, range: ScoreboardDateRange): boolean {
  const s = user.signals;
  return [
    s.createdAt,
    s.lastOpenedAt,
    s.onboardingCompletedAt,
    s.latestSpeechAt,
    s.trialStartedAt,
    s.trialEndsAt,
    s.paidAt,
    s.checkoutStartedAt,
    s.offerClickedAt,
    s.offerConvertedAt,
  ].some((value) => millisInDateRange(value, range)) || s.emailSentCount > 0 || s.purchaseAttemptCount > 0;
}

function aggregatePeriodOverlapsRange(period: AppsFlyerAggregatePeriodDoc, range: ScoreboardDateRange): boolean {
  if (period.excludedFromRangeRollups) return false;
  const start = scoreboardString(period.periodStart || period.from || getNestedValue(period, 'summary.from'));
  const end = scoreboardString(period.periodEnd || period.to || getNestedValue(period, 'summary.to')) || start;
  if (!start) return true;
  const periodStart = Date.parse(`${start.slice(0, 10)}T00:00:00`);
  const periodEnd = Date.parse(`${end.slice(0, 10)}T23:59:59.999`);
  const rangeStart = Date.parse(`${range.start}T00:00:00`);
  const rangeEnd = Date.parse(`${range.end}T23:59:59.999`);
  if (![periodStart, periodEnd, rangeStart, rangeEnd].every(Number.isFinite)) return true;
  return periodStart <= rangeEnd && periodEnd >= rangeStart;
}

function rawAppsFlyerEventDate(row: Record<string, any>): string {
  return scoreboardString(row.eventDate || row.event_date || row.date || row.day || row.installDate || row.install_time).slice(0, 10);
}

function rawAppsFlyerEventName(row: Record<string, any>): string {
  return scoreboardString(row.eventName || row.event_name || row.inAppEventName || row['In-apps events'] || row.event);
}

function rawAppsFlyerMediaSource(row: Record<string, any>): string {
  return scoreboardString(row.mediaSource || row.media_source || row['Media Source'] || row.source) || 'unknown';
}

function rawAppsFlyerCampaign(row: Record<string, any>): string {
  return scoreboardString(row.campaign || row.campaignName || row.campaign_name || row['Campaign']) || 'unknown';
}

function rawAppsFlyerCount(row: Record<string, any>): number {
  return scoreboardNumber(row.eventCount || row.event_count || row.count || row['Number Of Actions'] || row.actions) || 1;
}

function rawAppsFlyerRevenue(row: Record<string, any>): number {
  return scoreboardNumber(row.revenue || row.eventRevenue || row.event_revenue || row['Revenue']) || 0;
}

function emptyAppsFlyerSummary(range: ScoreboardDateRange, source: string): Record<string, any> {
  return {
    importSource: source,
    from: range.start,
    to: range.end,
    rows: 0,
    installs: { total: 0, organic: 0, nonOrganic: 0, byMediaSource: {} },
    events: { total: 0, byName: {}, byMediaSource: {}, byCampaign: {} },
    revenue: { total: 0 },
  };
}

function addAppsFlyerSummaryInto(target: Record<string, any>, source: Record<string, any>) {
  target.rows += scoreboardNumber(source.rows) || 0;
  target.installs.total += scoreboardNumber(getNestedValue(source, 'installs.total')) || 0;
  target.installs.organic += scoreboardNumber(getNestedValue(source, 'installs.organic')) || 0;
  target.installs.nonOrganic += scoreboardNumber(getNestedValue(source, 'installs.nonOrganic')) || 0;
  target.events.total += scoreboardNumber(getNestedValue(source, 'events.total')) || 0;
  target.revenue.total += scoreboardNumber(getNestedValue(source, 'revenue.total')) || 0;
  const maps: Array<[string, string]> = [
    ['installs.byMediaSource', 'installs.byMediaSource'],
    ['events.byName', 'events.byName'],
    ['eventCounts', 'events.byName'],
    ['events.byMediaSource', 'events.byMediaSource'],
    ['events.byCampaign', 'events.byCampaign'],
  ];
  maps.forEach(([sourcePath, targetPath]) => {
    const sourceMap = asRecord(getNestedValue(source, sourcePath));
    const targetMap = getNestedValue(target, targetPath) as Record<string, number> | undefined;
    if (!sourceMap || !targetMap) return;
    Object.entries(sourceMap).forEach(([key, value]) => {
      targetMap[key] = (targetMap[key] || 0) + (scoreboardNumber(value) || 0);
    });
  });
}

function summarizeAppsFlyerRawRows(rawRows: Record<string, any>[], range: ScoreboardDateRange): Record<string, any> | null {
  const rowsInRange = rawRows.filter((row) => {
    const date = rawAppsFlyerEventDate(row);
    return !date || (date >= range.start && date <= range.end);
  });
  if (!rowsInRange.length) return null;
  const summary = emptyAppsFlyerSummary(range, 'raw_rows');
  rowsInRange.forEach((row) => {
    const count = rawAppsFlyerCount(row);
    const eventName = rawAppsFlyerEventName(row);
    const mediaSource = rawAppsFlyerMediaSource(row);
    const campaign = rawAppsFlyerCampaign(row);
    summary.rows += 1;
    if (eventName) {
      summary.events.total += count;
      summary.events.byName[eventName] = (summary.events.byName[eventName] || 0) + count;
      summary.events.byMediaSource[mediaSource] = (summary.events.byMediaSource[mediaSource] || 0) + count;
      summary.events.byCampaign[campaign] = (summary.events.byCampaign[campaign] || 0) + count;
    } else {
      summary.installs.total += count;
      if (mediaSource === 'organic') summary.installs.organic += count;
      else summary.installs.nonOrganic += count;
      summary.installs.byMediaSource[mediaSource] = (summary.installs.byMediaSource[mediaSource] || 0) + count;
    }
    summary.revenue.total += rawAppsFlyerRevenue(row);
  });
  return summary;
}

function summarizeAppsFlyerAggregatePeriods(periods: AppsFlyerAggregatePeriodDoc[], range: ScoreboardDateRange): Record<string, any> | null {
  const matching = periods.filter((period) => aggregatePeriodOverlapsRange(period, range));
  if (!matching.length) return null;
  const summary = emptyAppsFlyerSummary(range, 'aggregate_periods');
  matching.forEach((period) => addAppsFlyerSummaryInto(summary, asRecord(period.summary) || period));
  summary.aggregateCsvPeriodCount = matching.length;
  return summary;
}

function chooseAppsFlyerSummary(args: {
  baseSummary: Record<string, any> | null;
  aggregatePeriods: AppsFlyerAggregatePeriodDoc[];
  rawRows: Record<string, any>[];
  range: ScoreboardDateRange;
}): Record<string, any> | null {
  const rawSummary = summarizeAppsFlyerRawRows(args.rawRows, args.range);
  if (rawSummary) return rawSummary;
  const aggregateSummary = summarizeAppsFlyerAggregatePeriods(args.aggregatePeriods, args.range);
  if (aggregateSummary) return aggregateSummary;
  return args.baseSummary;
}

function appsFlyerEventCount(summary: Record<string, any> | null, eventNames: string[]): number {
  if (!summary) return 0;
  const byName = asRecord(getNestedValue(summary, 'events.byName')) || asRecord(summary.eventCounts) || {};
  return eventNames.reduce((total, eventName) => total + (scoreboardNumber(byName[eventName]) || 0), 0);
}

function scoreboardSummary(state: ScoreboardState) {
  const users = state.users;
  const appsFlyerSummary = state.appsFlyerSummary;
  const activeAccess = users.filter((row) => row.signals.hasAppAccess).length;
  const trials = users.filter((row) => row.tier === 'trial_started').length;
  const totalSpeeches = users.reduce((sum, row) => sum + row.signals.totalSpeeches, 0);
  const speechUsers = users.filter((row) => row.signals.totalSpeeches > 0).length;
  const paywallIntentUsers = users.filter((row) => row.tier === 'paywall_intent').length;
  const checkoutUsers = users.filter((row) => row.signals.checkoutStartedAt || row.signals.purchaseAttemptCount > 0).length;
  const sentEmails = users.reduce((sum, row) => sum + row.signals.emailSentCount, 0);
  const openedEmails = users.reduce((sum, row) => sum + row.signals.emailOpenedCount, 0);
  const clickedEmails = users.reduce((sum, row) => sum + row.signals.emailClickedCount, 0);
  const purchaseSuccesses = users.reduce((sum, row) => sum + row.signals.purchaseSuccessCount, 0);
  const purchaseFailures = users.reduce((sum, row) => sum + row.signals.purchaseFailedCount, 0);
  const purchaseCancels = users.reduce((sum, row) => sum + row.signals.purchaseCanceledCount, 0);
  const eventTotal = scoreboardNumber(getNestedValue(appsFlyerSummary, 'events.total')) || 0;
  const revenue = scoreboardNumber(getNestedValue(appsFlyerSummary, 'revenue.total')) || 0;
  const installs = scoreboardNumber(getNestedValue(appsFlyerSummary, 'installs.total')) || 0;
  const eventsByName = asRecord(getNestedValue(appsFlyerSummary, 'events.byName')) || asRecord(appsFlyerSummary?.eventCounts) || {};
  const mediaSources = asRecord(getNestedValue(appsFlyerSummary, 'events.byMediaSource'))
    || asRecord(getNestedValue(appsFlyerSummary, 'installs.byMediaSource'))
    || {};
  const campaigns = asRecord(getNestedValue(appsFlyerSummary, 'events.byCampaign')) || {};
  return {
    usersLoaded: users.length,
    activeAccess,
    trials,
    totalSpeeches,
    speechUsers,
    paywallIntentUsers,
    checkoutUsers,
    sentEmails,
    openedEmails,
    clickedEmails,
    purchaseSuccesses,
    purchaseFailures,
    purchaseCancels,
    eventTotal,
    installs,
    revenue,
    appOpens: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_APP_OPEN_EVENTS),
    registrations: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_REGISTRATION_EVENTS),
    onboardingCompleted: Math.max(
      users.filter((row) => row.signals.onboardingCompletedAt).length,
      appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_ONBOARDING_EVENTS)
    ),
    recordCompletedEvents: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_RECORD_EVENTS),
    scoreEvents: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_SCORE_EVENTS),
    paywallEvents: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_PAYWALL_EVENTS),
    ctaEvents: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_CTA_EVENTS),
    planSelectedEvents: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_PLAN_SELECTED_EVENTS),
    checkoutEvents: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_CHECKOUT_EVENTS),
    trialEvents: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_TRIAL_EVENTS),
    purchaseEvents: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_PURCHASE_EVENTS),
    cancelEvents: appsFlyerEventCount(appsFlyerSummary, PARROT_APPSFLYER_CANCEL_EVENTS),
    topEvents: topEntriesFromMap(eventsByName as Record<string, number>),
    topMediaSources: topEntriesFromMap(mediaSources as Record<string, number>),
    topCampaigns: topEntriesFromMap(campaigns as Record<string, number>),
    usersByTier: users.reduce((out, user) => {
      out[user.tierLabel] = (out[user.tierLabel] || 0) + 1;
      return out;
    }, {} as Record<string, number>),
  };
}

export const EmailSequencesAdmin: React.FC = () => {
  const { user, signOutUser } = useAuth();
  const [message, setMessage] = useState<BannerMessage | null>(null);
  const [activeAdminTab, setActiveAdminTab] = useState<AdminViewTab>('scoreboard');
  const [scheduleTimeById, setScheduleTimeById] = useState<Record<string, string>>({});
  const [sequenceEnabledById, setSequenceEnabledById] = useState<Record<string, boolean>>({});
  const [savingSequenceConfigId, setSavingSequenceConfigId] = useState<string | null>(null);
  const [scoreboard, setScoreboard] = useState<ScoreboardState>({
    loading: false,
    error: '',
    loadedAt: null,
    users: [],
    appsFlyerSummary: null,
    emailLogCount: 0,
    purchaseLogCount: 0,
    userLimit: SCOREBOARD_USER_LIMIT,
  });
  const [scoreboardRangePreset, setScoreboardRangePreset] = useState<ScoreboardRangePreset>(SCOREBOARD_DEFAULT_RANGE_PRESET);
  const [scoreboardRangeStart, setScoreboardRangeStart] = useState(() => buildScoreboardDateRange(SCOREBOARD_DEFAULT_RANGE_PRESET).start);
  const [scoreboardRangeEnd, setScoreboardRangeEnd] = useState(() => buildScoreboardDateRange(SCOREBOARD_DEFAULT_RANGE_PRESET).end);
  const [copyingScoreboard, setCopyingScoreboard] = useState(false);

  const [activeSequence, setActiveSequence] = useState<SequenceRow | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateHtml, setTemplateHtml] = useState('');
  const [templateLoadedFromFirestore, setTemplateLoadedFromFirestore] = useState(false);
  const [templateUpdatedAt, setTemplateUpdatedAt] = useState('');

  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [testVarsJson, setTestVarsJson] = useState(JSON.stringify(DEFAULT_VARS, null, 2));
  const [sendingTest, setSendingTest] = useState(false);

  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleEditingSequence, setScheduleEditingSequence] = useState<SequenceRow | null>(null);
  const [scheduleTimeDraft, setScheduleTimeDraft] = useState('14:00');
  const [savingSchedule, setSavingSchedule] = useState(false);

  const scheduleOptions = useMemo(() => {
    const out: string[] = [];
    for (let h = 0; h < 24; h += 1) {
      out.push(`${String(h).padStart(2, '0')}:00`);
      out.push(`${String(h).padStart(2, '0')}:30`);
    }
    return out;
  }, []);

  const selectedScoreboardRange = useMemo<ScoreboardDateRange>(() => {
    const option = SCOREBOARD_RANGE_OPTIONS.find((row) => row.value === scoreboardRangePreset) || SCOREBOARD_RANGE_OPTIONS[0];
    return {
      preset: scoreboardRangePreset,
      start: scoreboardRangeStart,
      end: scoreboardRangeEnd,
      label: option.label,
      daysBack: scoreboardRangePreset === 'custom'
        ? dateOnlyDaySpan(scoreboardRangeStart, scoreboardRangeEnd)
        : option.daysBack,
    };
  }, [scoreboardRangeEnd, scoreboardRangePreset, scoreboardRangeStart]);

  const selectedScoreboardRangeLabel = useMemo(
    () => formatRangeLabel(selectedScoreboardRange),
    [selectedScoreboardRange]
  );

  const loadParrotScoreboard = useCallback(async () => {
    if (!db) {
      setScoreboard((prev) => ({
        ...prev,
        loading: false,
        error: 'Firestore is not configured.',
      }));
      return;
    }

    const database = db;
    const range = selectedScoreboardRange;
    setScoreboard((prev) => ({ ...prev, loading: true, error: '' }));

    try {
      let usersSnapshot: Awaited<ReturnType<typeof getDocs>>;
      try {
        usersSnapshot = await getDocs(query(collection(database, 'users'), orderBy('createdAt', 'desc'), limit(SCOREBOARD_USER_LIMIT)));
      } catch {
        // Some early users may not have createdAt. Fall back to a bounded read.
        usersSnapshot = await getDocs(query(collection(database, 'users'), limit(SCOREBOARD_USER_LIMIT)));
      }

      const loadRecentDocs = async (collectionName: string, orderField: string, rowLimit: number) => {
        try {
          const snapshot = await getDocs(query(collection(database, collectionName), orderBy(orderField, 'desc'), limit(rowLimit)));
          return snapshot.docs.map((snapshotDoc) => ({
            id: snapshotDoc.id,
            ...((snapshotDoc.data() || {}) as Record<string, any>),
          }));
        } catch (error) {
          console.warn(`[EmailSequencesAdmin] Failed to load ${collectionName} for Parrot scoreboard`, error);
          return [] as Record<string, any>[];
        }
      };

      const [
        appsFlyerSummarySnap,
        appsFlyerAggregatePeriodsSnap,
        appsFlyerRawRowsSnap,
        emailLogs,
        purchaseLogs,
      ] = await Promise.all([
        getDoc(doc(database, 'appsflyer-scoreboards', 'parrot')).catch((error) => {
          console.warn('[EmailSequencesAdmin] Failed to load Parrot AppsFlyer summary', error);
          return null;
        }),
        getDocs(query(collection(database, 'appsflyer-aggregate-periods'), where('product', '==', 'parrot'), limit(200))).catch((error) => {
          console.warn('[EmailSequencesAdmin] Failed to load Parrot AppsFlyer aggregate periods', error);
          return null;
        }),
        getDocs(query(collection(database, 'appsflyer-parrot-raw-rows'), where('product', '==', 'parrot'), limit(5000))).catch((error) => {
          console.warn('[EmailSequencesAdmin] Failed to load Parrot AppsFlyer raw rows', error);
          return null;
        }),
        loadRecentDocs('email-logs', 'updatedAt', SCOREBOARD_LOG_LIMIT),
        loadRecentDocs('Parrot-purchase-logs', 'createdAtEpoch', SCOREBOARD_LOG_LIMIT),
      ]);

      const allUserDocs = usersSnapshot.docs.map((snapshotDoc) => ({
        id: snapshotDoc.id,
        data: (snapshotDoc.data() || {}) as Record<string, any>,
      }));
      const parrotEmailLogs = emailLogs
        .filter(isParrotEmailLog)
        .filter((log) => millisInDateRange(emailLogMillis(log), range));
      const parrotPurchaseLogs = purchaseLogs
        .filter((log) => millisInDateRange(purchaseLogMillis(log), range));
      const appsFlyerAggregatePeriods: AppsFlyerAggregatePeriodDoc[] = appsFlyerAggregatePeriodsSnap?.docs.map((snapshotDoc) => ({
        id: snapshotDoc.id,
        ...((snapshotDoc.data() || {}) as Record<string, any>),
      })) || [];
      const appsFlyerRawRows = appsFlyerRawRowsSnap?.docs.map((snapshotDoc) => ({
        id: snapshotDoc.id,
        ...((snapshotDoc.data() || {}) as Record<string, any>),
      })) || [];
      const baseAppsFlyerSummary = appsFlyerSummarySnap?.exists()
        ? ((appsFlyerSummarySnap.data() || {}) as Record<string, any>)
        : null;
      const appsFlyerSummary = chooseAppsFlyerSummary({
        baseSummary: baseAppsFlyerSummary,
        aggregatePeriods: appsFlyerAggregatePeriods,
        rawRows: appsFlyerRawRows,
        range,
      });

      const allUsers = allUserDocs.map((row) => {
        const email = userEmail(row.data);
        const userEmailLogs = parrotEmailLogs.filter((log) => matchesUserOrEmail(log, row.id, email));
        const userPurchaseLogs = parrotPurchaseLogs.filter((log) => matchesUserOrEmail(log, row.id, email));
        return buildScoreboardUser({
          id: row.id,
          data: row.data,
          emailLogs: userEmailLogs,
          purchaseLogs: userPurchaseLogs,
        });
      });
      const rangeUsers = allUsers
        .filter((row) => userHasRangeActivity(row, range))
        .sort((a, b) => b.priority - a.priority || (b.signals.lastOpenedAt || 0) - (a.signals.lastOpenedAt || 0));

      setScoreboard({
        loading: false,
        error: '',
        loadedAt: new Date(),
        users: rangeUsers,
        appsFlyerSummary,
        emailLogCount: parrotEmailLogs.length,
        purchaseLogCount: parrotPurchaseLogs.length,
        userLimit: SCOREBOARD_USER_LIMIT,
      });
    } catch (error: any) {
      setScoreboard((prev) => ({
        ...prev,
        loading: false,
        error: error?.message || 'Failed to load Parrot scoreboard.',
      }));
    }
  }, [selectedScoreboardRange]);

  useEffect(() => {
    loadParrotScoreboard();
  }, [loadParrotScoreboard]);

  const updateScoreboardRangePreset = (preset: ScoreboardRangePreset) => {
    const nextRange = buildScoreboardDateRange(preset);
    setScoreboardRangePreset(preset);
    setScoreboardRangeStart(nextRange.start);
    setScoreboardRangeEnd(nextRange.end);
  };

  const parrotScoreboardSummary = useMemo(() => scoreboardSummary(scoreboard), [scoreboard]);

  const scoreboardMetricCards = useMemo(() => [
    {
      label: 'Users in range',
      value: formatCompactNumber(parrotScoreboardSummary.usersLoaded),
      sublabel: `${scoreboard.userLimit.toLocaleString()} user read limit`,
    },
    {
      label: 'Speech activity',
      value: formatCompactNumber(parrotScoreboardSummary.totalSpeeches),
      sublabel: `${parrotScoreboardSummary.speechUsers.toLocaleString()} users with completed speeches`,
    },
    {
      label: 'Active access',
      value: formatCompactNumber(parrotScoreboardSummary.activeAccess),
      sublabel: `${parrotScoreboardSummary.trials.toLocaleString()} trial users in range`,
    },
    {
      label: 'Email engagement',
      value: formatPercent(parrotScoreboardSummary.openedEmails, Math.max(1, parrotScoreboardSummary.sentEmails)),
      sublabel: `${parrotScoreboardSummary.sentEmails.toLocaleString()} sent / ${parrotScoreboardSummary.clickedEmails.toLocaleString()} clicked`,
    },
  ], [parrotScoreboardSummary, scoreboard.userLimit]);

  const scoreboardFunnelRows = useMemo(() => [
    {
      label: 'App opens',
      value: formatCompactNumber(parrotScoreboardSummary.appOpens || parrotScoreboardSummary.usersLoaded),
      sublabel: parrotScoreboardSummary.appOpens ? 'AppsFlyer app open events' : 'Fallback from loaded users',
    },
    {
      label: 'Signup / registration',
      value: formatCompactNumber(Math.max(parrotScoreboardSummary.registrations, parrotScoreboardSummary.usersLoaded)),
      sublabel: `${formatPercent(Math.max(parrotScoreboardSummary.registrations, parrotScoreboardSummary.usersLoaded), parrotScoreboardSummary.appOpens || parrotScoreboardSummary.usersLoaded)} of app opens`,
    },
    {
      label: 'Onboarding complete',
      value: formatCompactNumber(parrotScoreboardSummary.onboardingCompleted),
      sublabel: `${formatPercent(parrotScoreboardSummary.onboardingCompleted, Math.max(parrotScoreboardSummary.registrations, parrotScoreboardSummary.usersLoaded))} of registrations`,
    },
    {
      label: 'Completed speeches',
      value: formatCompactNumber(Math.max(parrotScoreboardSummary.recordCompletedEvents, parrotScoreboardSummary.speechUsers)),
      sublabel: `${parrotScoreboardSummary.scoreEvents.toLocaleString()} score events`,
    },
    {
      label: 'Paywall viewed',
      value: formatCompactNumber(Math.max(parrotScoreboardSummary.paywallEvents, parrotScoreboardSummary.paywallIntentUsers)),
      sublabel: `${parrotScoreboardSummary.ctaEvents.toLocaleString()} CTA taps - ${parrotScoreboardSummary.planSelectedEvents.toLocaleString()} plan selections`,
    },
    {
      label: 'Checkout started',
      value: formatCompactNumber(Math.max(parrotScoreboardSummary.checkoutEvents, parrotScoreboardSummary.checkoutUsers)),
      sublabel: `${formatPercent(Math.max(parrotScoreboardSummary.checkoutEvents, parrotScoreboardSummary.checkoutUsers), Math.max(parrotScoreboardSummary.ctaEvents, 1))} of CTA taps`,
    },
    {
      label: 'Trial / purchase',
      value: `${formatCompactNumber(parrotScoreboardSummary.trialEvents)} / ${formatCompactNumber(Math.max(parrotScoreboardSummary.purchaseEvents, parrotScoreboardSummary.purchaseSuccesses))}`,
      sublabel: `${formatCurrency(parrotScoreboardSummary.revenue)} AppsFlyer revenue`,
    },
  ], [parrotScoreboardSummary]);

  useEffect(() => {
    const loadSequenceConfig = async () => {
      if (!db) return;
      const timeUpdates: Record<string, string> = {};
      const enabledUpdates: Record<string, boolean> = {};
      try {
        for (const seq of SEQUENCES) {
          const snap = await getDoc(doc(db, 'email-sequence-config', sequenceConfigDocId(seq)));
          const data = snap.exists() ? snap.data() : {};
          enabledUpdates[seq.id] = typeof data.enabled === 'boolean' ? data.enabled : true;
          if (seq.scheduleConfigDocId) {
            timeUpdates[seq.id] = typeof data.sendTimeUtc === 'string'
              ? data.sendTimeUtc
              : '14:00';
          }
        }
        setScheduleTimeById(timeUpdates);
        setSequenceEnabledById(enabledUpdates);
      } catch {
        // Defaults are enough for display; saving will surface errors.
      }
    };
    loadSequenceConfig();
  }, []);

  const openEditModal = async (seq: SequenceRow) => {
    setActiveSequence(seq);
    setIsEditModalOpen(true);
    setMessage(null);
    setLoadingTemplate(true);
    setTemplateLoadedFromFirestore(false);
    setTemplateUpdatedAt('');
    try {
      if (!db) throw new Error('Firestore is not configured.');
      const snap = await getDoc(doc(db, 'email-templates', seq.templateDocId));
      if (snap.exists()) {
        const data = snap.data();
        setTemplateSubject(typeof data.subject === 'string' ? data.subject : seq.defaultSubject);
        setTemplateHtml(typeof data.html === 'string' ? data.html : starterHtml(seq));
        setTemplateLoadedFromFirestore(true);
        setTemplateUpdatedAt(prettyDate(data.updatedAt));
      } else {
        setTemplateSubject(seq.defaultSubject);
        setTemplateHtml(starterHtml(seq));
      }
    } catch (error: any) {
      setTemplateSubject(seq.defaultSubject);
      setTemplateHtml(starterHtml(seq));
      setMessage({ type: 'error', text: error?.message || 'Failed to load template.' });
    } finally {
      setLoadingTemplate(false);
    }
  };

  const saveTemplate = async () => {
    if (!activeSequence) return;
    if (!db) {
      setMessage({ type: 'error', text: 'Firestore is not configured.' });
      return;
    }
    if (!templateSubject.trim()) {
      setMessage({ type: 'error', text: 'Subject is required.' });
      return;
    }
    if (!templateHtml.trim()) {
      setMessage({ type: 'error', text: 'HTML is required.' });
      return;
    }
    setSavingTemplate(true);
    try {
      await setDoc(
        doc(db, 'email-templates', activeSequence.templateDocId),
        {
          id: activeSequence.templateDocId,
          sequenceId: activeSequence.id,
          subject: templateSubject.trim(),
          html: templateHtml,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || null,
        },
        { merge: true }
      );
      setTemplateLoadedFromFirestore(true);
      setTemplateUpdatedAt(new Date().toLocaleString());
      setMessage({ type: 'success', text: 'Template saved.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to save template.' });
    } finally {
      setSavingTemplate(false);
    }
  };

  const copyHtmlToClipboard = async () => {
    if (!templateHtml.trim()) {
      setMessage({ type: 'error', text: 'No HTML to copy.' });
      return;
    }
    try {
      await navigator.clipboard.writeText(templateHtml);
      setMessage({ type: 'success', text: 'HTML copied.' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to copy HTML.' });
    }
  };

  const resetToStarter = () => {
    if (!activeSequence) return;
    setTemplateSubject(activeSequence.defaultSubject);
    setTemplateHtml(starterHtml(activeSequence));
    setMessage({ type: 'info', text: 'Starter template loaded. Save it to persist.' });
  };

  const openTestModal = (seq: SequenceRow) => {
    setActiveSequence(seq);
    setTestEmail('');
    setTestName('');
    setTestVarsJson(JSON.stringify(DEFAULT_VARS, null, 2));
    setIsTestModalOpen(true);
    setMessage(null);
  };

  const sendTest = async () => {
    if (!activeSequence) return;
    if (!testEmail.trim()) {
      setMessage({ type: 'error', text: 'Enter a test recipient.' });
      return;
    }
    setSendingTest(true);
    setMessage(null);
    try {
      const authToken = await user?.getIdToken();
      if (!authToken) throw new Error('Admin auth token unavailable. Sign in again.');

      let vars: Record<string, any> = {};
      try {
        vars = testVarsJson.trim() ? JSON.parse(testVarsJson) : {};
      } catch {
        throw new Error('Template variables must be valid JSON.');
      }

      let subject = activeSequence.defaultSubject;
      let html = starterHtml(activeSequence);
      if (db) {
        const snap = await getDoc(doc(db, 'email-templates', activeSequence.templateDocId));
        if (snap.exists()) {
          const data = snap.data();
          subject = typeof data.subject === 'string' && data.subject.trim()
            ? data.subject.trim()
            : subject;
          html = typeof data.html === 'string' && data.html.trim()
            ? data.html
            : html;
        }
      }

      const resp = await fetch(activeSequence.functionPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          sequenceId: activeSequence.id,
          sequenceName: activeSequence.name,
          toEmail: testEmail.trim(),
          firstName: testName.trim() || vars.firstName || vars.first_name || undefined,
          subject,
          html,
          vars,
          isTest: true,
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(json?.error || `Failed to send test email (HTTP ${resp.status}).`);
      }
      setMessage({
        type: 'success',
        text: 'Test email accepted by Brevo. Check Brevo logs for delivery/rejection status.',
      });
      setIsTestModalOpen(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to send test email.' });
    } finally {
      setSendingTest(false);
    }
  };

  const openScheduleModal = (seq: SequenceRow) => {
    setScheduleEditingSequence(seq);
    setScheduleTimeDraft(scheduleTimeById[seq.id] || '14:00');
    setIsScheduleModalOpen(true);
    setMessage(null);
  };

  const saveScheduleTime = async () => {
    if (!scheduleEditingSequence?.scheduleConfigDocId) return;
    if (!db) {
      setMessage({ type: 'error', text: 'Firestore is not configured.' });
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(scheduleTimeDraft)) {
      setMessage({ type: 'error', text: 'Invalid time format.' });
      return;
    }
    setSavingSchedule(true);
    try {
      const enabled = sequenceEnabledById[scheduleEditingSequence.id] ?? true;
      await setDoc(
        doc(db, 'email-sequence-config', scheduleEditingSequence.scheduleConfigDocId),
        {
          id: scheduleEditingSequence.scheduleConfigDocId,
          sequenceId: scheduleEditingSequence.id,
          sendTimeUtc: scheduleTimeDraft,
          enabled,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || null,
        },
        { merge: true }
      );
      setScheduleTimeById((prev) => ({ ...prev, [scheduleEditingSequence.id]: scheduleTimeDraft }));
      setSequenceEnabledById((prev) => ({ ...prev, [scheduleEditingSequence.id]: enabled }));
      setMessage({ type: 'success', text: `Schedule saved: ${scheduleTimeDraft} UTC.` });
      setIsScheduleModalOpen(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to save schedule.' });
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleSequenceEnabled = async (seq: SequenceRow, enabled: boolean) => {
    if (!db) {
      setMessage({ type: 'error', text: 'Firestore is not configured.' });
      return;
    }

    const configDocId = sequenceConfigDocId(seq);
    setSavingSequenceConfigId(seq.id);
    setMessage(null);
    try {
      await setDoc(
        doc(db, 'email-sequence-config', configDocId),
        {
          id: configDocId,
          sequenceId: seq.id,
          ...(seq.scheduleConfigDocId
            ? { sendTimeUtc: scheduleTimeById[seq.id] || '14:00' }
            : {}),
          enabled,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || null,
        },
        { merge: true }
      );
      setSequenceEnabledById((prev) => ({ ...prev, [seq.id]: enabled }));
      setMessage({
        type: 'success',
        text: `${seq.name} ${enabled ? 'enabled' : 'paused'}.`,
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.message || `Failed to ${enabled ? 'enable' : 'pause'} sequence.`,
      });
    } finally {
      setSavingSequenceConfigId(null);
    }
  };

  const copyScoreboardReport = async () => {
    setCopyingScoreboard(true);
    setMessage(null);
    try {
      const summary = parrotScoreboardSummary;
      const usersByTier = topEntriesFromMap(summary.usersByTier, 8)
        .map(([tier, count]) => `- ${tier}: ${count}`)
        .join('\n') || '- No user tiers loaded';
      const topEvents = summary.topEvents
        .map(([eventName, count]) => `- ${eventName}: ${count}`)
        .join('\n') || '- No AppsFlyer events loaded';
      const priorityUsers = scoreboard.users.slice(0, 8)
        .map((row, index) => `${index + 1}. ${row.displayName} (${row.email || row.id}) - ${row.tierLabel}; ${row.nextEmail?.label || 'No suggested email'}`)
        .join('\n') || 'No prioritized users loaded.';
      const report = [
        `Parrot Scoreboard - ${selectedScoreboardRangeLabel}`,
        `Generated: ${new Date().toLocaleString()}`,
        '',
        'Summary',
        `- Users in range: ${summary.usersLoaded}`,
        `- Speech users: ${summary.speechUsers}`,
        `- Completed speeches visible: ${summary.totalSpeeches}`,
        `- Active access: ${summary.activeAccess}`,
        `- Email sends: ${summary.sentEmails}`,
        `- Email opens: ${summary.openedEmails}`,
        `- Email clicks: ${summary.clickedEmails}`,
        `- Purchase successes: ${summary.purchaseSuccesses}`,
        `- AppsFlyer events: ${summary.eventTotal}`,
        `- AppsFlyer revenue: ${formatCurrency(summary.revenue)}`,
        '',
        'Funnel',
        `- App opens: ${summary.appOpens}`,
        `- Registrations: ${summary.registrations}`,
        `- Onboarding completed: ${summary.onboardingCompleted}`,
        `- Speech completed events: ${summary.recordCompletedEvents}`,
        `- Paywall events: ${summary.paywallEvents}`,
        `- CTA taps: ${summary.ctaEvents}`,
        `- Plan selections: ${summary.planSelectedEvents}`,
        `- Checkout starts: ${summary.checkoutEvents}`,
        `- Trial starts: ${summary.trialEvents}`,
        `- Purchase events: ${summary.purchaseEvents}`,
        '',
        'Users By Tier',
        usersByTier,
        '',
        'Top AppsFlyer Events',
        topEvents,
        '',
        'Priority Users',
        priorityUsers,
      ].join('\n');
      await navigator.clipboard.writeText(report);
      setMessage({ type: 'success', text: 'Parrot scoreboard report copied.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to copy scoreboard report.' });
    } finally {
      setCopyingScoreboard(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Email Sequences</div>
            <div className="text-xs text-neutral-500">
              Brevo-backed templates, test sends, and UTC schedule config.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 font-mono">{user?.email}</span>
            <button
              onClick={signOutUser}
              className="text-xs text-neutral-600 underline hover:text-neutral-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <AdminTabs current="emailSequences" />
        </div>

        {message && (
          <div
            className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
              message.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : message.type === 'error'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="mb-6 flex flex-wrap gap-2 border-b border-neutral-200 pb-3">
          {[
            { id: 'scoreboard' as const, label: 'Parrot scoreboard', detail: `${scoreboard.users.length} users` },
            { id: 'sequences' as const, label: 'Email sequences', detail: `${SEQUENCES.length} emails` },
          ].map((tab) => {
            const active = activeAdminTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveAdminTab(tab.id)}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  active
                    ? 'bg-neutral-900 text-white'
                    : 'bg-white text-neutral-600 border border-neutral-200 hover:border-neutral-400'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] ${active ? 'bg-white/15 text-white/80' : 'bg-neutral-100 text-neutral-500'}`}>
                  {tab.detail}
                </span>
              </button>
            );
          })}
        </div>

        {activeAdminTab === 'scoreboard' ? (
          <section className="space-y-6">
            <div className="rounded-xl border border-neutral-200 bg-white p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-xl font-bold text-neutral-900">Parrot Scoreboard</div>
                  <div className="mt-1 text-sm text-neutral-500">
                    Acquisition, speech activity, subscription intent, and email follow-up for the selected window.
                  </div>
                  <div className="mt-2 text-xs text-neutral-400">
                    {scoreboard.loadedAt ? `Refreshed ${formatScoreboardAgo(scoreboard.loadedAt)}` : 'Waiting for first load'} - {selectedScoreboardRangeLabel}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={scoreboardRangePreset}
                    onChange={(event) => updateScoreboardRangePreset(event.target.value as ScoreboardRangePreset)}
                    className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm outline-none focus:border-neutral-900"
                    aria-label="Parrot scoreboard date range"
                  >
                    {SCOREBOARD_RANGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {scoreboardRangePreset === 'custom' ? (
                    <>
                      <input
                        type="date"
                        value={scoreboardRangeStart}
                        onChange={(event) => setScoreboardRangeStart(event.target.value)}
                        className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm outline-none focus:border-neutral-900"
                        aria-label="Scoreboard start date"
                      />
                      <input
                        type="date"
                        value={scoreboardRangeEnd}
                        onChange={(event) => setScoreboardRangeEnd(event.target.value)}
                        className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm outline-none focus:border-neutral-900"
                        aria-label="Scoreboard end date"
                      />
                    </>
                  ) : (
                    <div className="flex h-10 items-center rounded-lg border border-neutral-200 bg-neutral-50 px-3 text-sm text-neutral-500">
                      {selectedScoreboardRangeLabel}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={copyScoreboardReport}
                    disabled={copyingScoreboard || (!scoreboard.loadedAt && !scoreboard.users.length)}
                    className="h-10 rounded-lg border border-neutral-200 bg-white px-4 text-sm font-semibold text-neutral-700 hover:border-neutral-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {copyingScoreboard ? 'Copying...' : 'Copy report'}
                  </button>
                  <button
                    type="button"
                    onClick={loadParrotScoreboard}
                    disabled={scoreboard.loading}
                    className="h-10 rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {scoreboard.loading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
              </div>
            </div>

            {scoreboard.error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {scoreboard.error}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {scoreboardMetricCards.map((card) => (
                <div key={card.label} className="rounded-xl border border-neutral-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{card.label}</div>
                  <div className="mt-2 text-3xl font-black text-neutral-900">{card.value}</div>
                  <div className="mt-1 text-xs text-neutral-500">{card.sublabel}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="xl:col-span-2 rounded-xl border border-neutral-200 bg-white overflow-hidden">
                <div className="border-b border-neutral-100 px-5 py-4">
                  <div className="text-sm font-bold text-neutral-900">Funnel Readout</div>
                  <div className="mt-1 text-xs text-neutral-500">
                    Uses AppsFlyer Parrot events when available, with local user data as a fallback.
                  </div>
                </div>
                <div className="divide-y divide-neutral-100">
                  {scoreboardFunnelRows.map((row) => (
                    <div key={row.label} className="grid grid-cols-1 gap-1 px-5 py-3 sm:grid-cols-[190px_1fr_240px] sm:items-center">
                      <div className="text-sm font-semibold text-neutral-700">{row.label}</div>
                      <div className="text-2xl font-black text-neutral-900">{row.value}</div>
                      <div className="text-xs text-neutral-500 sm:text-right">{row.sublabel}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
                <div className="border-b border-neutral-100 px-5 py-4">
                  <div className="text-sm font-bold text-neutral-900">AppsFlyer Validation</div>
                  <div className="mt-1 text-xs text-neutral-500">
                    {scoreboard.appsFlyerSummary
                      ? `${formatCompactNumber(parrotScoreboardSummary.eventTotal)} events - ${formatCurrency(parrotScoreboardSummary.revenue)}`
                      : 'No Parrot AppsFlyer summary loaded yet'}
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-neutral-50 p-3">
                      <div className="text-xs uppercase tracking-wide text-neutral-400">Installs</div>
                      <div className="mt-1 text-xl font-black text-neutral-900">{formatCompactNumber(parrotScoreboardSummary.installs)}</div>
                    </div>
                    <div className="rounded-lg bg-neutral-50 p-3">
                      <div className="text-xs uppercase tracking-wide text-neutral-400">Events</div>
                      <div className="mt-1 text-xl font-black text-neutral-900">{formatCompactNumber(parrotScoreboardSummary.eventTotal)}</div>
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">Top events</div>
                    {parrotScoreboardSummary.topEvents.length ? (
                      <div className="space-y-2">
                        {parrotScoreboardSummary.topEvents.map(([eventName, value]) => (
                          <div key={eventName} className="flex items-center justify-between gap-3 text-sm">
                            <span className="truncate text-neutral-600">{titleizeToken(eventName)}</span>
                            <span className="font-semibold text-neutral-900">{formatCompactNumber(value)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-neutral-500">Import a Parrot AppsFlyer summary to populate events.</div>
                    )}
                  </div>
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">Media sources</div>
                    {parrotScoreboardSummary.topMediaSources.length ? (
                      <div className="space-y-2">
                        {parrotScoreboardSummary.topMediaSources.map(([source, value]) => (
                          <div key={source} className="flex items-center justify-between gap-3 text-sm">
                            <span className="truncate text-neutral-600">{source}</span>
                            <span className="font-semibold text-neutral-900">{formatCompactNumber(value)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-neutral-500">No media source rollup is available yet.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
                <div className="border-b border-neutral-100 px-5 py-4">
                  <div className="text-sm font-bold text-neutral-900">User Tiers</div>
                  <div className="mt-1 text-xs text-neutral-500">Priority groups for follow-up.</div>
                </div>
                <div className="p-5 space-y-2">
                  {topEntriesFromMap(parrotScoreboardSummary.usersByTier, 10).length ? (
                    topEntriesFromMap(parrotScoreboardSummary.usersByTier, 10).map(([tier, count]) => (
                      <div key={tier} className="flex items-center justify-between gap-3 rounded-lg bg-neutral-50 px-3 py-2 text-sm">
                        <span className="font-semibold text-neutral-700">{tier}</span>
                        <span className="font-black text-neutral-900">{count}</span>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-neutral-500">No users matched this date window.</div>
                  )}
                </div>
              </div>

              <div className="xl:col-span-2 rounded-xl border border-neutral-200 bg-white overflow-hidden">
                <div className="border-b border-neutral-100 px-5 py-4">
                  <div className="text-sm font-bold text-neutral-900">Priority Users</div>
                  <div className="mt-1 text-xs text-neutral-500">
                    Suggested email lane is inferred from Parrot activity, subscription state, and recent logs.
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 border-b border-neutral-100">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">User</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Tier</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Signals</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Suggested email</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {scoreboard.loading ? (
                        <tr>
                          <td colSpan={4} className="px-5 py-8 text-sm text-neutral-500">Loading scoreboard...</td>
                        </tr>
                      ) : scoreboard.users.length ? (
                        scoreboard.users.slice(0, 60).map((row) => (
                          <tr key={row.id} className="hover:bg-neutral-50">
                            <td className="px-5 py-4">
                              <div className="font-semibold text-neutral-900">{row.displayName}</div>
                              <div className="mt-1 font-mono text-[11px] text-neutral-400">{row.email || row.id}</div>
                              <div className="mt-1 text-xs text-neutral-400">Last open: {formatScoreboardDate(row.signals.lastOpenedAt)}</div>
                            </td>
                            <td className="px-5 py-4">
                              <span className="inline-flex rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs font-semibold text-neutral-700">
                                {row.tierLabel}
                              </span>
                              <div className="mt-2 text-xs text-neutral-400">{row.suggestedLane}</div>
                            </td>
                            <td className="px-5 py-4 text-xs text-neutral-500">
                              <div>{row.signals.totalSpeeches.toLocaleString()} completed speeches</div>
                              <div>{row.signals.currentStreak.toLocaleString()} day streak</div>
                              <div>{row.signals.purchaseAttemptCount.toLocaleString()} purchase log entries</div>
                              <div>{row.signals.emailSentCount.toLocaleString()} emails sent</div>
                            </td>
                            <td className="px-5 py-4">
                              {row.nextEmail ? (
                                <div>
                                  <div className="font-semibold text-neutral-900">{row.nextEmail.label}</div>
                                  <div className="mt-1 font-mono text-[11px] text-neutral-400">{row.nextEmail.sequenceId}</div>
                                  <div className="mt-2 text-xs text-neutral-500">{row.nextEmail.reason}</div>
                                </div>
                              ) : (
                                <div className="text-sm text-neutral-400">No follow-up needed</div>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="px-5 py-8 text-sm text-neutral-500">No users matched this scoreboard window.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-100">
              <div className="text-sm font-semibold text-neutral-900">Sequence List</div>
              <div className="text-xs text-neutral-500 mt-1">
                Templates save to <span className="font-mono">email-templates</span>; schedule controls save to{' '}
                <span className="font-mono">email-sequence-config</span>. Paused sequences remain editable and testable.
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Trigger</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Subject</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {SEQUENCES.map((seq) => {
                    const enabled = sequenceEnabledById[seq.id] ?? true;
                    const isSavingThisConfig = savingSequenceConfigId === seq.id;
                    return (
                      <tr key={seq.id} className={enabled ? 'hover:bg-neutral-50' : 'bg-neutral-50/70 hover:bg-neutral-50'}>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-neutral-900">{seq.name}</div>
                          <div className="font-mono text-[11px] text-neutral-400 mt-1">{seq.templateDocId}</div>
                        </td>
                        <td className="px-6 py-4 text-neutral-600 min-w-[280px]">
                          {seq.trigger}
                          {seq.scheduleConfigDocId ? (
                            <div className="text-xs text-neutral-400 mt-1">
                              Scheduled: {scheduleTimeById[seq.id] || '14:00'} UTC
                            </div>
                          ) : null}
                          <div className="text-xs text-neutral-400 mt-1">
                            Vars: {seq.variables.map((v) => `{{${v}}}`).join(', ')}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-neutral-600 min-w-[240px]">{seq.defaultSubject}</td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                              enabled
                                ? 'bg-green-50 text-green-700 border border-green-100'
                                : 'bg-neutral-100 text-neutral-500 border border-neutral-200'
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                enabled ? 'bg-green-500' : 'bg-neutral-400'
                              }`}
                            />
                            {enabled ? 'Enabled' : 'Paused'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => toggleSequenceEnabled(seq, !enabled)}
                              disabled={isSavingThisConfig}
                              className={`px-3 py-2 rounded-lg text-xs font-semibold transition disabled:opacity-50 ${
                                enabled
                                  ? 'bg-white border border-neutral-200 hover:border-neutral-400 text-neutral-700'
                                  : 'bg-green-600 text-white hover:bg-green-700'
                              }`}
                            >
                              {isSavingThisConfig ? 'Saving...' : enabled ? 'Pause' : 'Enable'}
                            </button>
                            {seq.scheduleConfigDocId ? (
                              <button
                                onClick={() => openScheduleModal(seq)}
                                className="px-3 py-2 bg-white border border-neutral-200 hover:border-neutral-400 rounded-lg text-xs font-semibold transition"
                              >
                                Schedule
                              </button>
                            ) : null}
                            <button
                              onClick={() => openEditModal(seq)}
                              className="px-3 py-2 bg-white border border-neutral-200 hover:border-neutral-400 rounded-lg text-xs font-semibold transition"
                            >
                              View / edit
                            </button>
                            <button
                              onClick={() => openTestModal(seq)}
                              className="px-3 py-2 bg-neutral-900 text-white hover:bg-neutral-800 rounded-lg text-xs font-semibold transition"
                            >
                              Send test
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {isEditModalOpen && activeSequence && (
        <div className="fixed inset-0 z-50 bg-black/60 p-4 flex items-center justify-center">
          <div className="bg-white border border-neutral-200 rounded-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between gap-4">
              <div>
                <div className="text-lg font-bold">Edit email template</div>
                <div className="text-xs text-neutral-500">
                  {activeSequence.name}
                  {templateLoadedFromFirestore ? ' - Saved template' : ' - Starter template'}
                  {templateUpdatedAt ? ` - ${templateUpdatedAt}` : ''}
                </div>
              </div>
              <button
                onClick={() => setIsEditModalOpen(false)}
                disabled={loadingTemplate || savingTemplate}
                className="text-sm text-neutral-600 hover:text-neutral-900"
              >
                Close
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[72vh]">
              {loadingTemplate ? (
                <div className="py-16 text-center text-sm text-neutral-500">Loading template...</div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600 mb-1">Subject</label>
                      <input
                        value={templateSubject}
                        onChange={(e) => setTemplateSubject(e.target.value)}
                        className="w-full px-3 py-2.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <label className="block text-xs font-semibold text-neutral-600">HTML</label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={resetToStarter}
                            className="text-xs text-neutral-500 underline hover:text-neutral-900"
                          >
                            Load starter
                          </button>
                          <button
                            type="button"
                            onClick={copyHtmlToClipboard}
                            className="text-xs text-neutral-500 underline hover:text-neutral-900"
                          >
                            Copy HTML
                          </button>
                        </div>
                      </div>
                      <textarea
                        value={templateHtml}
                        onChange={(e) => setTemplateHtml(e.target.value)}
                        className="w-full h-[520px] px-3 py-3 text-xs font-mono border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400 resize-none"
                      />
                      <div className="text-xs text-neutral-400 mt-2">
                        Supported variables: {activeSequence.variables.map((v) => `{{${v}}}`).join(', ')}.
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-neutral-600">Preview</div>
                    <div className="border border-neutral-200 rounded-xl overflow-hidden bg-neutral-50">
                      <iframe
                        title="Email preview"
                        srcDoc={templateHtml || '<p style="font-family:Arial;color:#777">No HTML</p>'}
                        style={{ width: '100%', height: 620, border: 'none', background: '#fff' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-end gap-3">
              <button
                onClick={() => setIsEditModalOpen(false)}
                disabled={savingTemplate || loadingTemplate}
                className="px-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm font-semibold hover:border-neutral-400"
              >
                Close
              </button>
              <button
                onClick={saveTemplate}
                disabled={savingTemplate || loadingTemplate || !templateSubject.trim() || !templateHtml.trim()}
                className="px-4 py-2 bg-neutral-900 text-white rounded-lg text-sm font-semibold disabled:opacity-40 hover:bg-neutral-800"
              >
                {savingTemplate ? 'Saving...' : 'Save template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isTestModalOpen && activeSequence && (
        <div className="fixed inset-0 z-50 bg-black/60 p-4 flex items-center justify-center">
          <div className="bg-white border border-neutral-200 rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
              <div>
                <div className="text-lg font-bold">Send test email</div>
                <div className="text-xs text-neutral-500">{activeSequence.name}</div>
              </div>
              <button
                onClick={() => setIsTestModalOpen(false)}
                disabled={sendingTest}
                className="text-sm text-neutral-600 hover:text-neutral-900"
              >
                Close
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1">Test recipient</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={sendingTest}
                  className="w-full px-3 py-2.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1">First name</label>
                <input
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  placeholder="Tremaine"
                  disabled={sendingTest}
                  className="w-full px-3 py-2.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1">Template variables JSON</label>
                <textarea
                  value={testVarsJson}
                  onChange={(e) => setTestVarsJson(e.target.value)}
                  disabled={sendingTest}
                  className="w-full h-44 px-3 py-3 text-xs font-mono border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400 resize-none"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-end gap-3">
              <button
                onClick={() => setIsTestModalOpen(false)}
                disabled={sendingTest}
                className="px-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm font-semibold hover:border-neutral-400"
              >
                Cancel
              </button>
              <button
                onClick={sendTest}
                disabled={sendingTest || !testEmail.trim()}
                className="px-4 py-2 bg-neutral-900 text-white rounded-lg text-sm font-semibold disabled:opacity-40 hover:bg-neutral-800"
              >
                {sendingTest ? 'Sending...' : 'Send test'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isScheduleModalOpen && scheduleEditingSequence && (
        <div className="fixed inset-0 z-50 bg-black/60 p-4 flex items-center justify-center">
          <div className="bg-white border border-neutral-200 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-neutral-100">
              <div className="text-lg font-bold">Schedule email</div>
              <div className="text-xs text-neutral-500">{scheduleEditingSequence.name} - UTC</div>
            </div>
            <div className="p-6">
              <label className="block text-xs font-semibold text-neutral-600 mb-1">Daily send time</label>
              <select
                value={scheduleTimeDraft}
                onChange={(e) => setScheduleTimeDraft(e.target.value)}
                disabled={savingSchedule}
                className="w-full px-3 py-2.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400 bg-white"
              >
                {scheduleOptions.map((time) => (
                  <option key={time} value={time}>
                    {time} UTC
                  </option>
                ))}
              </select>
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-end gap-3">
              <button
                onClick={() => setIsScheduleModalOpen(false)}
                disabled={savingSchedule}
                className="px-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm font-semibold hover:border-neutral-400"
              >
                Cancel
              </button>
              <button
                onClick={saveScheduleTime}
                disabled={savingSchedule}
                className="px-4 py-2 bg-neutral-900 text-white rounded-lg text-sm font-semibold disabled:opacity-40 hover:bg-neutral-800"
              >
                {savingSchedule ? 'Saving...' : 'Save time'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
